import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useLiveDocsBlock } from '@reflex/plugin-docs/web/blocks/live';
import type { Blueprint, CreateBlueprintRequest } from '@/lib/types';
import { BUILDING_STATUSES } from './useBlueprintFilters';
import { blueprintsFixture } from './useBlueprints.fixture';

/**
 * Reads the org's blueprint list. Routed through the canonical PRD-9a
 * `useLiveDocsBlock` adapter so anonymous readers (and the simulation
 * shell on `/demos/:slug`) see a fixture rather than a network call,
 * while signed-in callers continue to read live workspace data with the
 * same `refetchInterval` polling behavior they had before.
 *
 * The return shape mirrors `UseQueryResult` enough that all existing
 * callers (`{ data: blueprints = [] } = useBlueprints()`) continue to
 * destructure cleanly. `isError` collapses to the live-fallback indicator
 * (`isFallback`) — when the live request fails, callers see the fixture
 * with `isError: true`.
 */
export function useBlueprints() {
  const orgKey = useOrgKey();
  const result = useLiveDocsBlock<Blueprint[]>({
    queryKey: queryKeys.blueprints(orgKey),
    queryFn: api.listBlueprints,
    fixture: blueprintsFixture,
    refetchInterval: (query) => {
      const data = query.state.data as Blueprint[] | undefined;
      const hasBuilding = data?.some((bp) => BUILDING_STATUSES.includes(bp.status));
      return hasBuilding ? 5000 : false;
    },
  });
  return {
    data: result.isLoading ? [] : result.data,
    isLoading: result.isLoading,
    isError: result.isFallback,
    error: result.error,
    isFallback: result.isFallback,
    source: result.source,
  };
}

export function useBlueprint(id: string | undefined) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: [...queryKeys.blueprint(orgKey, id ?? '')],
    queryFn: () => api.getBlueprint(id!),
    enabled: !!id,
  });
}

export function useCreateBlueprint() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (data: CreateBlueprintRequest) => api.createBlueprint(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.blueprints(orgKey) });
    },
  });
}

/**
 * Rebuild an existing blueprint from its stored parameters. The server
 * replays every build input (including build args and secrets) under the same
 * name, so the client doesn't have to reconstruct a create payload and can't
 * drop fields it can't fully see. Invalidates the list so the new build row
 * surfaces with its in-flight status.
 */
export function useRebuildBlueprint() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => api.rebuildBlueprint(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.blueprints(orgKey) });
    },
  });
}

export function useDeleteBlueprint() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => api.deleteBlueprint(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<Blueprint[]>(
        queryKeys.blueprints(orgKey),
        (old) => old?.filter((p) => p.id !== id) ?? [],
      );
    },
  });
}

export function useBlueprintLogs(id: string | null, options?: { pollingEnabled?: boolean }) {
  const orgKey = useOrgKey();
  const pollingEnabled = options?.pollingEnabled ?? true;
  return useQuery({
    queryKey: [...queryKeys.blueprintLogs(orgKey, id!)],
    queryFn: () => api.getBlueprintLogs(id!),
    enabled: !!id,
    refetchInterval: pollingEnabled ? 5000 : false,
  });
}
